import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { toast } from "sonner";
import { CalendarClock, Plus, RefreshCw, RotateCcw, Trash2, ShieldAlert } from "lucide-react";
import { api, apiError } from "../lib/api";
import { Btn, Input, Label, Modal, Select, Table, Td, Empty, Badge } from "./UI";
import { SlidePanel } from "./SlidePanel";
import { ProviderSelect } from "./ProviderSelect";

const WEEKDAY_OPTIONS = [
  ["0", "Mon"], ["1", "Tue"], ["2", "Wed"], ["3", "Thu"], ["4", "Fri"], ["5", "Sat"], ["6", "Sun"],
];

const EMPTY = {
  room_id: "", provider_name: "", provider_id: "", purpose: "",
  start_date: dayjs().format("YYYY-MM-DD"),
  end_date: dayjs().add(30, "day").format("YYYY-MM-DD"),
  start_time: "09:00", end_time: "10:00",
  frequency: "weekly", days_of_week: [0, 1, 2, 3, 4], notes: "",
};

function daysLabel(freq, days) {
  if (freq === "daily") return "Every day";
  if (!days || days.length === 0) return "-";
  const map = { 0: "Mon", 1: "Tue", 2: "Wed", 3: "Thu", 4: "Fri", 5: "Sat", 6: "Sun" };
  const s = new Set(days.map(Number));
  if ([0, 1, 2, 3, 4].every((d) => s.has(d)) && s.size === 5) return "Mon-Fri";
  return [0, 1, 2, 3, 4, 5, 6].filter((d) => s.has(d)).map((d) => map[d]).join(", ");
}

export function RoomBookingsPanel({ open, onClose, facilities, rooms, isAdmin, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ facility_id: "", room_id: "" });
  const [editing, setEditing] = useState(null); // booking id being edited, or "new"
  const [form, setForm] = useState(EMPTY);
  const [conflicts, setConflicts] = useState([]);
  const [overrideReason, setOverrideReason] = useState("");
  const [busy, setBusy] = useState(false);

  const facilityMap = useMemo(() => Object.fromEntries(facilities.map((f) => [f.id, f.name])), [facilities]);

  const load = () => {
    setLoading(true);
    const params = {};
    if (filter.facility_id) params.facility_id = filter.facility_id;
    if (filter.room_id) params.room_id = filter.room_id;
    api.get("/room-bookings", { params })
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (open) load(); }, [open, filter.facility_id, filter.room_id]);

  const openNew = () => {
    setForm(EMPTY);
    setConflicts([]); setOverrideReason("");
    setEditing("new");
  };
  const openEdit = (row) => {
    setForm({
      room_id: row.room_id, provider_name: row.provider_name, provider_id: row.provider_id || "",
      purpose: row.purpose || "", start_date: row.start_date, end_date: row.end_date,
      start_time: row.start_time, end_time: row.end_time,
      frequency: row.frequency || "weekly",
      days_of_week: (row.days_of_week || []).map(Number),
      notes: row.notes || "",
    });
    setConflicts([]); setOverrideReason(row.override_reason || "");
    setEditing(row.id);
  };
  const closeEditor = () => { setEditing(null); setForm(EMPTY); setConflicts([]); setOverrideReason(""); };

  const toggleDow = (v) => {
    const n = Number(v);
    setForm((s) => {
      const has = s.days_of_week.includes(n);
      return { ...s, days_of_week: has ? s.days_of_week.filter((x) => x !== n) : [...s.days_of_week, n].sort((a, b) => a - b) };
    });
  };

  const preview = async () => {
    setBusy(true);
    try {
      const r = await api.post("/room-bookings/preview-conflicts", form);
      setConflicts(r.data.conflicts || []);
      if ((r.data.conflicts || []).length === 0) toast.success("No appointment conflicts on this recurring window");
      else toast.warning(`${r.data.count} appointment conflict${r.data.count === 1 ? "" : "s"} detected`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        // Daily frequency does not use the day-of-week list; send an empty array so the
        // server does not persist stale weekday selections when switching Weekly -> Daily
        days_of_week: form.frequency === "daily" ? [] : form.days_of_week,
        override_reason: overrideReason,
      };
      if (editing === "new") {
        await api.post("/room-bookings", payload);
        toast.success("Recurring block created");
      } else {
        await api.put(`/room-bookings/${editing}`, payload);
        toast.success("Recurring block updated");
      }
      closeEditor();
      load();
      if (typeof onChange === "function") onChange();
    } catch (e) {
      // Backend returns 409 with { message, conflicts } when there are conflicts
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail && typeof detail === "object") {
        setConflicts(detail.conflicts || []);
        toast.error(detail.message || "Conflicts detected");
      } else {
        toast.error(apiError(e));
      }
    } finally { setBusy(false); }
  };

  const cancel = async (row) => {
    if (!window.confirm(`Cancel the recurring block for ${row.provider_name}? Existing appointments will not be affected.`)) return;
    try {
      await api.delete(`/room-bookings/${row.id}`);
      toast.success("Recurring block cancelled");
      load();
      if (typeof onChange === "function") onChange();
    } catch (e) { toast.error(apiError(e)); }
  };

  const filteredRooms = useMemo(() => (
    filter.facility_id ? rooms.filter((r) => r.facility_id === filter.facility_id) : rooms
  ), [rooms, filter.facility_id]);

  const roomsForForm = useMemo(() => rooms.filter((r) => r.status !== "archived"), [rooms]);

  return (
    <>
      <Modal open={open && !editing} onClose={onClose} title="Recurring Room Bookings" wide>
        <div data-testid="room-bookings-panel" className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Label>Facility</Label>
              <Select data-testid="room-bookings-facility-filter" value={filter.facility_id}
                onChange={(e) => setFilter({ facility_id: e.target.value, room_id: "" })}
                className="!h-8 !text-xs">
                <option value="">All facilities</option>
                {facilities.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label>Room</Label>
              <Select data-testid="room-bookings-room-filter" value={filter.room_id}
                onChange={(e) => setFilter({ ...filter, room_id: e.target.value })}
                className="!h-8 !text-xs">
                <option value="">All rooms</option>
                {filteredRooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </Select>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Btn variant="outline" data-testid="room-bookings-refresh" onClick={load} className="!h-8 !px-3 !text-xs">
                <RefreshCw size={12} /> Refresh
              </Btn>
              <Btn data-testid="room-bookings-add-btn" onClick={openNew} className="!h-8 !px-3 !text-xs">
                <Plus size={12} /> New Recurring Block
              </Btn>
            </div>
          </div>
          {loading ? (
            <Empty text="Loading recurring blocks…" />
          ) : items.length === 0 ? (
            <Empty text='No recurring blocks yet. Click "New Recurring Block" to reserve a room for a physician on a repeating schedule.' />
          ) : (
            <div className="-mx-1 overflow-x-auto">
            <Table headers={["Provider", "Room", "Window", "Time", "Repeats", "Actions"]}>
              {items.map((row) => (
                <tr key={row.id} data-testid={`room-booking-row-${row.id}`}>
                  <Td>
                    <div className="font-semibold text-[#3E2723]">{row.provider_name}</div>
                    {row.purpose && <div className="text-[11px] text-[#7B6157]">{row.purpose}</div>}
                  </Td>
                  <Td>
                    <div>{row.room_name}</div>
                    {row.facility_id && <div className="text-[11px] text-[#7B6157]">{facilityMap[row.facility_id] || ""}</div>}
                  </Td>
                  <Td>
                    <div>{dayjs(row.start_date).format("MMM D")} <span className="text-[#84675B]">to</span> {dayjs(row.end_date).format("MMM D, YYYY")}</div>
                    {row.override_reason && (
                      <div className="mt-0.5 flex items-center gap-1 text-[10px] text-[#8D5A00]">
                        <ShieldAlert size={10} /> Overridden: {row.override_reason}
                      </div>
                    )}
                  </Td>
                  <Td>{row.start_time} - {row.end_time}</Td>
                  <Td>
                    <Badge value={row.frequency || "weekly"} />
                    <div className="mt-0.5 text-[11px] text-[#6D4C41]">{daysLabel(row.frequency, row.days_of_week)}</div>
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      <button type="button" onClick={() => openEdit(row)}
                        data-testid={`room-booking-edit-${row.id}`}
                        className="rounded-full bg-[#F5F2EB] px-2 py-1 text-[10px] font-semibold text-[#4E342E] hover:bg-[#EFEBE0]"
                        aria-label={`Edit recurring block for ${row.provider_name}`}>
                        <RotateCcw size={10} /> Edit
                      </button>
                      <button type="button" onClick={() => cancel(row)}
                        data-testid={`room-booking-cancel-${row.id}`}
                        className="rounded-full bg-[#FFEBEE] px-2 py-1 text-[10px] font-semibold text-[#C62828] hover:bg-[#FFCDD2]"
                        aria-label={`Cancel recurring block for ${row.provider_name}`}>
                        <Trash2 size={10} /> Cancel
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
            </Table>
            </div>
          )}
        </div>
      </Modal>

      <SlidePanel open={!!editing} onClose={closeEditor}
        title={editing === "new" ? "New Recurring Room Block" : "Edit Recurring Room Block"}
        subtitle="Reserve a room for a physician on a daily or weekly schedule"
        testId="room-booking-editor">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Provider Name *</Label>
              <ProviderSelect testId="rb-provider" value={form.provider_name}
                onChange={(v) => setForm((s) => ({ ...s, provider_name: v }))}
                onPick={(row) => setForm((s) => ({ ...s, provider_name: row.name, provider_id: row.id || "" }))}
                placeholder="Search prescribers by name, org, or NPI" />
            </div>
            <div><Label>Room *</Label>
              <Select data-testid="rb-room" value={form.room_id}
                onChange={(e) => setForm((s) => ({ ...s, room_id: e.target.value }))}>
                <option value="">Select room…</option>
                {roomsForForm.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}{r.unit ? ` - ${r.unit}` : ""}</option>
                ))}
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start Date *</Label>
              <Input type="date" data-testid="rb-start-date" value={form.start_date}
                onChange={(e) => setForm((s) => ({ ...s, start_date: e.target.value }))} />
            </div>
            <div><Label>End Date *</Label>
              <Input type="date" data-testid="rb-end-date" value={form.end_date}
                onChange={(e) => setForm((s) => ({ ...s, end_date: e.target.value }))} />
            </div>
            <div><Label>Start Time *</Label>
              <Input type="time" data-testid="rb-start-time" value={form.start_time}
                onChange={(e) => setForm((s) => ({ ...s, start_time: e.target.value }))} />
            </div>
            <div><Label>End Time *</Label>
              <Input type="time" data-testid="rb-end-time" value={form.end_time}
                onChange={(e) => setForm((s) => ({ ...s, end_time: e.target.value }))} />
            </div>
          </div>
          <div>
            <Label>Frequency</Label>
            <div className="flex gap-2">
              {["weekly", "daily"].map((f) => (
                <button type="button" key={f} data-testid={`rb-freq-${f}`}
                  onClick={() => setForm((s) => ({ ...s, frequency: f }))}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${form.frequency === f ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#EFEBE0]"}`}>
                  {f === "weekly" ? "Weekly" : "Daily"}
                </button>
              ))}
            </div>
          </div>
          {form.frequency === "weekly" && (
            <div>
              <Label>Days of Week</Label>
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAY_OPTIONS.map(([v, l]) => {
                  const on = form.days_of_week.includes(Number(v));
                  return (
                    <button type="button" key={v} data-testid={`rb-dow-${v}`}
                      onClick={() => toggleDow(v)}
                      className={`rounded-lg px-3 py-1 text-xs font-semibold transition-colors ${on ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#EFEBE0]"}`}>{l}</button>
                  );
                })}
              </div>
            </div>
          )}
          <div>
            <Label>Purpose</Label>
            <Input data-testid="rb-purpose" placeholder="e.g., Buprenorphine clinic, Group therapy, Med management"
              value={form.purpose} onChange={(e) => setForm((s) => ({ ...s, purpose: e.target.value }))} />
          </div>
          <div>
            <Label>Notes</Label>
            <textarea data-testid="rb-notes" rows={3}
              className="w-full rounded-xl border border-[#EFEBE0] bg-white p-2 text-sm outline-none focus:ring-2 focus:ring-[#8D6E63]"
              value={form.notes} onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))} />
          </div>

          {conflicts.length > 0 && (
            <div data-testid="rb-conflict-warning" className="rounded-xl border border-[#FFB300] bg-[#FFF8E1] p-3 text-xs text-[#8D6E00]">
              <div className="mb-1 flex items-center gap-1 font-bold">
                <CalendarClock size={12} /> {conflicts.length} existing appointment{conflicts.length === 1 ? "" : "s"} overlap this block
              </div>
              <div className="max-h-32 overflow-y-auto space-y-0.5">
                {conflicts.slice(0, 10).map((c, i) => (
                  <div key={`${c.appointment_id}-${i}`} className="tabular-nums">
                    {dayjs(c.date).format("MMM D")} · {c.start_time}-{c.end_time} · {c.client_name || "-"} ({c.type || "-"})
                  </div>
                ))}
                {conflicts.length > 10 && <div className="text-[10px] text-[#8D5A00]">+{conflicts.length - 10} more</div>}
              </div>
              {isAdmin ? (
                <div className="mt-2">
                  <Label>Override reason (required to force save)</Label>
                  <Input data-testid="rb-override-reason" value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)} placeholder="e.g., Coverage confirmed with providers" />
                </div>
              ) : (
                <div className="mt-1 text-[11px] font-semibold text-[#8D5A00]">
                  Only an admin can override. Resolve the appointments first, or ask an admin to force save.
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between gap-3 border-t border-[#EFEBE0] pt-4">
            <Btn variant="outline" data-testid="rb-preview-conflicts" onClick={preview} disabled={busy}>
              <CalendarClock size={12} /> Preview conflicts
            </Btn>
            <div className="flex gap-2">
              <Btn variant="ghost" onClick={closeEditor}>Cancel</Btn>
              <Btn data-testid="rb-save-btn" onClick={save} disabled={busy}>
                {busy ? "Saving…" : (editing === "new" ? "Create Block" : "Save Changes")}
              </Btn>
            </div>
          </div>
        </div>
      </SlidePanel>
    </>
  );
}
